// useParams reads a React context, so it must come from the same module instance the
// <Routes> tree renders from - react-router-dom, as everywhere else in the app. The bare
// "react-router" specifier is a second entry point and need not resolve to that instance.
import { generatePath, type PathParam } from "react-router";
import { useParams, type Params } from "react-router-dom";

/**
 * The pathname part of a path string, dropping any query string and fragment.
 * `matchPath` matches a pathname, so a path pushed with a query has to be cut back
 * to one before any registry lookup.
 */
export function pathnameOf(path: string): string {
  return path.split(/[?#]/, 1)[0] ?? path;
}

/**
 * How a route is presented under native navigation. `push` adds it to the current
 * stack; the others present it as a native modal (matching the plugin's
 * presentation styles). Defaults to `push`.
 */
export type RoutePresentation = "push" | "pageSheet" | "formSheet" | "fullScreen" | "dialog";

/** A sheet is inset over the stack, so it lays out in less than the full screen. */
export function isSheetPresentation(present: RoutePresentation | undefined): boolean {
  return present === "pageSheet" || present === "formSheet";
}

/** The tab whose stack a route lives in, if it belongs to one. */
export type RouteTab = "home" | "chat" | "team" | "account";

export interface RouteMeta {
  /** Requires an authenticated session. One flag per route, read by a single guard. */
  requiresAuth?: boolean;
  /**
   * The path is referenced externally (OAuth redirect, emailed auth / invite
   * links), so it must not be renamed without changing whatever points at it.
   */
  external?: boolean;
  /** The tab whose stack this route belongs to. */
  tab?: RouteTab;
  /** How the route is shown under native navigation. Defaults to `push`. */
  present?: RoutePresentation;
  /**
   * Extra path patterns that stay within this route's modal stack rather than
   * dismissing it. Only meaningful when `present` is set: the native navigator
   * matches a push target's path against the modal's own path first, and only
   * treats it as "leaving the modal" if nothing matches. A modal whose children
   * push to sibling paths (e.g. `/edit` presenting a sheet that then navigates
   * to `/edit/sections/:sectionId`) must list those paths here, or the child
   * push dismisses the sheet and lands on the underlying root stack instead.
   */
  modalMatch?: string[];
  /**
   * Push a second view for this path even when one is open (iOS only). Routes with
   * `present` never de-duplicate, so the flag is redundant on those.
   */
  allowDuplicate?: boolean;
  /**
   * Hide the native tab bar while this route is on top of its stack (iOS only). Must
   * be readable at push time (see `viewOptionsForPath` in nativeNavigation/init.ts):
   * the plugin only honours this if it's set before the view is pushed, so it can't
   * be applied reactively from within the page once it's already on screen.
   */
  hidesTabBar?: boolean;
  /**
   * Hide the native navigation bar (top bar with back button, title, etc.) while this
   * route is on top of its stack (iOS only). Same push-time constraint as hidesTabBar.
   */
  hidesNavBar?: boolean;
  /** Belongs to the pre-auth sign-in/sign-up flow, or is the OAuth callback that completes it. */
  authFlow?: boolean;
}

type ParamValue = string | number;

/**
 * The arguments `build` takes, derived from the path template. A path with no
 * params builds with no arguments; a path with params requires them. The param
 * names come from `PathParam`, so we never hand-write the generics.
 */
export type BuildArgs<P extends string> = [PathParam<P>] extends [never]
  ? []
  : [params: Record<PathParam<P>, ParamValue>];

export interface RouteNode<P extends string = string> {
  /** The canonical path template, kebab-case. */
  readonly path: P;
  readonly meta: RouteMeta;
  /**
   * Build a concrete URL. Path params are checked against the path template.
   * An optional second argument adds query parameters to the URL.
   *
   * ```ts
   * route("/items/:itemId").build({ itemId: "123" })
   *   // "/items/123"
   * route("/auth/sign-up/details").build({}, { email: "foo@bar.com" })
   *   // "/auth/sign-up/details?email=foo@bar.com"
   * ```
   */
  build(params?: Record<string, ParamValue>, query?: Record<string, string>): string;
}

/**
 * Declare a route. The `const` type parameter captures the path as a literal, so
 * `PathParam` can extract its params and `build` is typed against them.
 *
 * ```ts
 * const room = route("/chat/:type/:roomId", { tab: "chat" })
 * room.build({ type: "channel", roomId: "abc" }) // "/chat/channel/abc"
 * room.build({ roomId: "abc" })                   // type error: missing 'type'
 * ```
 */
export function route<const P extends string>(path: P, meta: RouteMeta = {}): RouteNode<P> {
  return {
    path,
    meta,
    build: (params: Record<string, ParamValue> = {}, query?: Record<string, string>): string => {
      const base = interpolate(path, params);
      if (query && Object.keys(query).length > 0) {
        const qs = new URLSearchParams(query).toString();
        return `${base}?${qs}`;
      }
      return base;
    },
  };
}

/**
 * Read the current route's params, typed against a route from the registry instead of
 * a bare, hand-written type argument. Reading a key the route's path template doesn't
 * declare is a compile error rather than a value that is silently `undefined`.
 *
 * ```ts
 * const { itemId } = useRouteParams(routes.edit.activityItem);
 * ```
 */
export function useRouteParams<P extends string>(_route: RouteNode<P>): Params<PathParam<P>> {
  // _route only drives inference of P. The cast is needed because TS cannot prove
  // `[PathParam<P>] extends [string]` for a generic P, though this is the runtime shape.
  return useParams<PathParam<P>>() as Params<PathParam<P>>;
}

/**
 * Fill a path template with params. Shared by `route().build` and the path-string
 * navigation helpers in nav.ts so there is one implementation.
 */
export function interpolate(path: string, params: Record<string, ParamValue>): string {
  const stringParams: Record<string, string> = {};
  for (const key of Object.keys(params)) {
    stringParams[key] = String(params[key]);
  }
  return generatePath(path, stringParams);
}
